import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  createTeamMember,
  listTeam,
  TeamMember,
  updateTeamMember,
} from "../api/physician";

// Physician's team (English UI): test_administrator (nurse/assistant) accounts.
// They work from the iPad app; their patients and tests appear in the
// physician's dashboard.
export default function PhysicianTeamPage() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);

  const load = useCallback(async () => {
    try {
      setMembers(await listTeam());
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">My Team</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateOpen(true)}>
          Add
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Nurse accounts for the iPad app: they register patients and run tests,
        and the results appear in your dashboard.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : members.length === 0 ? (
        <Typography sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
          No team members yet
        </Typography>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email (login)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{[m.name, m.surname].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    {m.is_active ? (
                      <Chip label="Active" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Disabled" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditMember(m)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <CreateMemberDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => { setCreateOpen(false); await load(); }}
      />
      <EditMemberDialog
        member={editMember}
        onClose={() => setEditMember(null)}
        onSaved={async () => { setEditMember(null); await load(); }}
      />
    </Box>
  );
}

function CreateMemberDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    setError("");
    setSaving(true);
    try {
      await createTeamMember({ email: email.trim(), password, name: name.trim(), surname: surname.trim() });
      setName(""); setSurname(""); setEmail(""); setPassword("");
      await onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>New nurse / assistant</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <TextField fullWidth margin="dense" label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <TextField fullWidth margin="dense" label="Surname" value={surname} onChange={(e) => setSurname(e.target.value)} required />
        <TextField fullWidth margin="dense" label="Email (iPad login)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <TextField fullWidth margin="dense" label="Password (min. 6 characters)" type="text" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving || !name.trim() || !surname.trim() || !email.trim() || password.length < 6}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditMemberDialog({
  member,
  onClose,
  onSaved,
}: {
  member: TeamMember | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (member) {
      setName(member.name || "");
      setSurname(member.surname || "");
      setPassword("");
      setIsActive(member.is_active);
      setError("");
    }
  }, [member]);

  const handleSave = async () => {
    if (!member) return;
    setError("");
    setSaving(true);
    try {
      const body: { name?: string; surname?: string; password?: string; is_active?: boolean } = {};
      if (name.trim() && name.trim() !== (member.name || "")) body.name = name.trim();
      if (surname.trim() && surname.trim() !== (member.surname || "")) body.surname = surname.trim();
      if (password) body.password = password;
      if (isActive !== member.is_active) body.is_active = isActive;
      if (Object.keys(body).length > 0) {
        await updateTeamMember(member.id, body);
      }
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!member} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Edit account</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {member?.email}
        </Typography>
        <TextField fullWidth margin="dense" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField fullWidth margin="dense" label="Surname" value={surname} onChange={(e) => setSurname(e.target.value)} />
        <TextField
          fullWidth margin="dense" label="New password (blank = unchanged)"
          type="text" value={password} onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          size="small"
          color={isActive ? "error" : "success"}
          onClick={() => setIsActive(!isActive)}
          sx={{ mt: 1 }}
        >
          {isActive ? "Disable account" : "Enable account"}
        </Button>
        {!isActive && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            A disabled account cannot sign in to the iPad app
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (password.length > 0 && password.length < 6)}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
